import React, { useEffect, useState } from "react";
import { authHeader } from "../_helpers";
import axios from "axios";
import moment from "moment";
import { getFormJuridique } from "../_helpers";
import Sidebar from "./component/Sidebar"; 
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

export default function DepositAccountDetails() {
  const { id } = useParams();
  const [filing, setFiling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    fetchFilingDetails();
  }, [id]);

  const fetchFilingDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}api/admin/deposit-accounts/${id}`,
        {
          headers: authHeader(),
        }
      );

      if (response.data && response.data.success) {
        setFiling(response.data.data);
      } else {
        setError("Dépôt de comptes non trouvé");
      }
    } catch (error) {
      console.error("Error fetching filing details:", error);
      setError(
        error.response?.data?.message ||
          "Erreur lors du chargement des détails"
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      draft: "default",
      signed: "info",
      submitted: "primary",
      validated: "success",
      rejected: "error",
      pending: "warning",
    };
    return statusColors[status] || "default";
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      draft: "Brouillon",
      signed: "Signé",
      submitted: "Soumis",
      validated: "Validé",
      rejected: "Rejeté",
      pending: "En attente",
    };
    return statusLabels[status] || status;
  };

  const getPaymentStatusColor = (paymentStatus) => {
    const colors = {
      succeeded: "success",
      monthly: "info",
      pending: "warning",
      failed: "error",
    };
    return colors[paymentStatus] || "default";
  };

  const getPaymentStatusLabel = (paymentStatus) => {
    const labels = {
      succeeded: "Payé",
      monthly: "Forfait mensuel",
      pending: "En attente",
      failed: "Échoué",
    };
    return labels[paymentStatus] || paymentStatus || "En attente";
  };

  if (loading) {
    return (
      <div className="flex w-full h-screen items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (error || !filing) {
    return (
      <div className="flex w-full h-screen items-center justify-center">
        <Alert severity="error">{error || "Dépôt de comptes non trouvé"}</Alert>
      </div>
    );
  }

  return (
    <div className="flex w-full h-screen overflow-hidden bg-gray-100">
      <div className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <Breadcrumbs aria-label="breadcrumb" className="mb-4">
              <Link to="/admin/home" className="text-blue-600 hover:underline">
                Tableau de bord
              </Link>
              <Link
                to="/admin/deposit-accounts"
                className="text-blue-600 hover:underline"
              >
                Dépôt des comptes
              </Link>
              <Typography color="text.primary">Détails #{filing.id}</Typography>
            </Breadcrumbs>

            <div className="flex justify-between items-center">
              <Typography variant="h4" component="h1" className="font-bold">
                Dépôt de comptes #{filing.id}
              </Typography>
              <Link to="/admin/deposit-accounts">
                <Button variant="outlined" className="text-sm xl:text-base font-medium text-primary hover:text-secondary" startIcon={<ArrowBackIcon />}>
                  Retour
                </Button>
              </Link>
            </div>
          </div>

          {/* Status Cards */}
          <Grid container spacing={3} className="mb-6">
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" className="mb-2">
                    Statut du dépôt
                  </Typography>
                  <Chip
                    label={getStatusLabel(filing.status)}
                    color={getStatusColor(filing.status)}
                    size="large"
                     sx={{
                            backgroundColor: "#1cbe93",   // change this
                            color: "white"
                          }}
                  />
                  {filing.inpiReferenceNumber && (
                    <Typography variant="body2" className="mt-2">
                      <strong>Référence INPI:</strong>{" "}
                      {filing.inpiReferenceNumber}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" className="mb-2">
                    Statut du paiement
                  </Typography>
                  <Chip
                    label={getPaymentStatusLabel(filing.paymentStatus)}
                    color={getPaymentStatusColor(filing.paymentStatus)}
                    size="large"
                     sx={{
                            backgroundColor: "#1cbe93",   // change this
                            color: "white"
                          }}
                  />
                  {filing.amountPaid && (
                    <Typography variant="body2" className="mt-2">
                      <strong>Montant:</strong>{" "}
                      {parseFloat(filing.amountPaid).toFixed(2)} €
                    </Typography>
                  )}
                  {filing.packageType && (
                    <Typography variant="body2">
                      <strong>Forfait:</strong> {filing.packageType}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Company Information */}
          <Card className="mb-6">
            <CardContent>
              <Typography variant="h6" className="mb-4 flex items-center gap-2">
                <BusinessIcon /> Informations de l'entreprise
              </Typography>
              <Divider className="mb-4" />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Nom de l'entreprise
                  </Typography>
                  <Typography variant="body1" className="font-semibold">
                    {filing.companyName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    SIREN
                  </Typography>
                  <Typography variant="body1" className="font-semibold">
                    {filing.siren || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Forme juridique
                  </Typography>
                  <Typography variant="body1">
                    {getFormJuridique(filing.legalForm) || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Adresse
                  </Typography>
                  <Typography variant="body1">
                    {filing.companyAddress || "N/A"}
                    {filing.companyPostalCode && `, ${filing.companyPostalCode}`}
                    {filing.companyCity && ` ${filing.companyCity}`}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* User Information */}
          <Card className="mb-6">
            <CardContent>
              <Typography variant="h6" className="mb-4 flex items-center gap-2">
                <PersonIcon /> Informations de l'utilisateur
              </Typography>
              <Divider className="mb-4" />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Nom complet
                  </Typography>
                  <Typography variant="body1" className="font-semibold">
                    {filing.userFirstname} {filing.userLastname}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{filing.userEmail}</Typography>
                </Grid>
                {filing.userTelephone && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Téléphone
                    </Typography>
                    <Typography variant="body1">
                      {filing.userTelephone}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Filing Details */}
          <Card className="mb-6">
            <CardContent>
              <Typography variant="h6" className="mb-4 flex items-center gap-2">
                <ReceiptIcon /> Détails du dépôt
              </Typography>
              <Divider className="mb-4" />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Type de dépôt
                  </Typography>
                  <Typography variant="body1" className="font-semibold">
                    {filing.filingType}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Type de liasse
                  </Typography>
                  <Typography variant="body1" className="font-semibold">
                    {filing.liasseType}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date de clôture
                  </Typography>
                  <Typography variant="body1">
                    {filing.closingDate
                      ? moment(filing.closingDate, "DD/MM/YYYY").format(
                          "DD/MM/YYYY"
                        )
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Chiffre d'affaires
                  </Typography>
                  <Typography variant="body1">
                    {filing.revenue
                      ? `${parseFloat(filing.revenue).toLocaleString()} €`
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total actif
                  </Typography>
                  <Typography variant="body1">
                    {filing.totalAssets
                      ? `${parseFloat(filing.totalAssets).toLocaleString()} €`
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total passif
                  </Typography>
                  <Typography variant="body1">
                    {filing.totalLiabilities
                      ? `${parseFloat(
                          filing.totalLiabilities
                        ).toLocaleString()} €`
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Résultat net
                  </Typography>
                  <Typography variant="body1">
                    {filing.netIncome
                      ? `${parseFloat(filing.netIncome).toLocaleString()} €`
                      : "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="mb-6">
            <CardContent>
              <Typography variant="h6" className="mb-4 flex items-center gap-2">
                <CalendarTodayIcon /> Chronologie
              </Typography>
              <Divider className="mb-4" />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date de création
                  </Typography>
                  <Typography variant="body1">
                    {filing.createdAt
                      ? moment(filing.createdAt).format("DD/MM/YYYY")
                      : "N/A"}
                  </Typography>
                </Grid>
                {filing.submittedAt && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Date de soumission
                    </Typography>
                    <Typography variant="body1">
                      {moment(filing.submittedAt).format("DD/MM/YYYY")}
                    </Typography>
                  </Grid>
                )}
                {filing.paidAt && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Date de paiement
                    </Typography>
                    <Typography variant="body1">
                      {moment(filing.paidAt).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                  </Grid>
                )}
                {filing.validatedAt && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Date de validation
                    </Typography>
                    <Typography variant="body1">
                      {moment(filing.validatedAt).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                  </Grid>
                )}
                {filing.rejectionReason && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      <Typography variant="body2" color="text.secondary">
                        Raison du rejet
                      </Typography>
                      <Typography variant="body1">
                        {filing.rejectionReason}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Documents */}
          {(filing.balanceSheetUrl ||
            filing.incomeStatementUrl ||
            filing.invoiceUrl ||
            (filing.attachments && filing.attachments.length > 0)) && (
            <Card className="mb-6">
              <CardContent>
                <Typography
                  variant="h6"
                  className="mb-4 flex items-center gap-2"
                >
                  <AttachFileIcon /> Documents
                </Typography>
                <Divider className="mb-4" />
                <Grid container spacing={2}>
                  {filing.balanceSheetUrl && (
                    <Grid item xs={12} md={6}>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        fullWidth
                        href={filing.balanceSheetUrl}
                        target="_blank"
                        className="text-sm xl:text-base font-medium text-primary hover:text-secondary"
                      >
                        Bilan
                      </Button>
                    </Grid>
                  )}
                  {filing.incomeStatementUrl && (
                    <Grid item xs={12} md={6}>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        fullWidth
                        href={filing.incomeStatementUrl}
                        target="_blank"
                        className="text-sm xl:text-base font-medium text-primary hover:text-secondary"
                      >
                        Compte de résultat
                      </Button>
                    </Grid>
                  )}
                  {filing.invoiceUrl && (
                    <Grid item xs={12} md={6}>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        fullWidth
                        href={filing.invoiceUrl}
                        target="_blank"
                        className="text-sm xl:text-base font-medium text-primary hover:text-secondary"
                      >
                        Facture ({filing.invoiceNumber})
                      </Button>
                    </Grid>
                  )}
                </Grid>

                {/* Attachments Table */}
                {filing.attachments && filing.attachments.length > 0 && (
                  <Box className="mt-4">
                    <Typography variant="subtitle1" className="mb-2">
                      Pièces jointes
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Type de document</TableCell>
                            <TableCell>Nom du fichier</TableCell>
                            <TableCell>Date d'upload</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filing.attachments.map((attachment) => (
                            <TableRow key={attachment.id}>
                              <TableCell>{attachment.documentType}</TableCell>
                              <TableCell>{attachment.fileName}</TableCell>
                              <TableCell>
                                {moment(attachment.uploadedAt).format(
                                  "DD/MM/YYYY HH:mm"
                                )}
                              </TableCell>
                              <TableCell>
                                {attachment.fileUrl && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    href={attachment.fileUrl}
                                    target="_blank"
                                    className="text-sm xl:text-base font-medium text-primary hover:text-secondary"
                                  >
                                    Télécharger
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Payment Information */}
          {filing.paymentIntentId && (
            <Card className="mb-6">
              <CardContent>
                <Typography variant="h6" className="mb-4">
                  Informations de paiement
                </Typography>
                <Divider className="mb-4" />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      ID de paiement Stripe
                    </Typography>
                    <Typography
                      variant="body1"
                      className="font-mono text-sm break-all"
                    >
                      {filing.paymentIntentId}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
